/* lib/api/salesConsole.js — консоль отдела продаж: тарифы (закрытые и публичные) и ссылки на оплату.
 *
 * Отдельный модуль от subscription.js намеренно: тот — клиентский контур (свой тариф,
 * свои платежи, свой чекаут) и ходит с X-Workspace-Id. Здесь staff-контур: он смотрит
 * НА ЧУЖИЕ пространства, и заголовок пространства ему не нужен вовсе — цель платежа
 * всегда приходит явным workspace_id в теле. Смешивать их в одном файле значит
 * рано или поздно приклеить wsHeaders туда, где он молча подменит адресата.
 *
 * Контракт клиента: apiFetch всегда возвращает {ok, status, data} и не бросает на HTTP-кодах.
 * Суммы (price_rub/amount_rub) — строки DRF, не числа.
 */

import { apiFetch } from './client.js';
import { consentBody } from '../recurringConsent.js';

/* ── Закрытые тарифы (право subscriptions.manage_private_plans) ── */

// GET /console/plans/ → {plans:[...], audiences:[{value,label}]}
// Отдаёт ВСЕ тарифы, включая публичные: менеджер сравнивает закрытые условия с витриной.
export const listConsolePlans = () => apiFetch('/api/subscription/console/plans/');

// POST /console/plans/ {slug, name, price_rub, ...} → 201 карточка тарифа.
// Созданный отсюда тариф по умолчанию закрытый и бизнесовый.
export const createConsolePlan = (payload) =>
  apiFetch('/api/subscription/console/plans/', { method: 'POST', body: payload });

// GET /console/plans/sellable/ → {plans:[{slug,name,price_rub,period_days,renewable,
//   audience,is_private,sales_note,chat_messages_per_30d,voice_seconds_per_30d}]}
// Право — issue_payment_links (НЕ manage_private_plans). Отдельный эндпоинт нужен
// именно потому, что права независимы: менеджеру по продажам редактор тарифов не
// открывают, а без списка тарифов он не выставит ни одной ссылки.
// Только активные и только платные — бесплатный тариф ссылкой не выставляют.
export const listSellablePlans = () => apiFetch('/api/subscription/console/plans/sellable/');

// PATCH /console/plans/<slug>/ — частичное обновление, шлём только изменённые поля.
export const updateConsolePlan = (slug, payload) =>
  apiFetch(`/api/subscription/console/plans/${encodeURIComponent(slug)}/`, {
    method: 'PATCH', body: payload,
  });

/* ── Публичные тарифы (право subscriptions.manage_public_plans) ──
   Контракт: work/console-plans-public/contract-w2-console-public-plans.json.
   Отдельные эндпоинты от закрытых намеренно: права независимы, и PATCH здесь
   с побочными эффектами (закрепление прежней цены за автопродлениями), которых у
   закрытых нет. Закрытый слаг сюда — 404, публичный в закрытые — 403. */

const PUBLIC_PLANS = '/api/subscription/console/public-plans/';

// GET → {groups:[{audience,label,families:[{key,name,is_promo,plans:[…]}]}], features_catalog}
// ВСЕ публичные тарифы, включая выключенные, бесплатные и тариф по умолчанию.
export const listPublicPlans = () => apiFetch(PUBLIC_PLANS);

// GET /<slug>/ → один тариф (свежие счётчики — для подтверждения перед сохранением).
export const getPublicPlan = (slug) => apiFetch(`${PUBLIC_PLANS}${encodeURIComponent(slug)}/`);

// POST {mode:'term'|'new'|'copy', …поля режима} → 201 тариф. Созданный ВСЕГДА снят
// с продажи (D3), что бы ни пришло в теле. 409 slug_taken, 400 invalid_payload|unknown_base.
export const createPublicPlan = (body) => apiFetch(PUBLIC_PLANS, { method: 'POST', body });

// PATCH /<slug>/ — только изменённые поля → тариф + {pinned_now, affected_now}.
// 400 frozen_field {fields:[…]} — срок/автопродление/аудитория/«после текущего»
// у тарифа с подписчиками; 400 default_plan_locked — выключение тарифа по умолчанию.
export const patchPublicPlan = (slug, body) =>
  apiFetch(`${PUBLIC_PLANS}${encodeURIComponent(slug)}/`, { method: 'PATCH', body });

/* Нормализаторы ответа. Ключи — ровно контрактные (snake_case): сверка «бэкенд
   отдаёт эти ключи — клиент читает эти» идёт по одному словарю (S13). Терпимость —
   к пустоте и мусору (экран не падает от одного кривого тарифа), а не к чужим
   именам полей: переименование должен ловить тест на фикстуре контракта. */

const PUBLIC_AUDIENCE_LABELS = { personal: 'Личные (B2C)', business: 'Бизнес (B2B)' };

// Decimal приходит строкой; мусор — null (форматтер напечатает «—», а не «0 ₽»,
// который читался бы как «бесплатно»).
const numOrNull = (v) => {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const intOrNull = (v) => {
  const n = numOrNull(v);
  return n == null ? null : Math.trunc(n);
};
// Счётчики подписок: «не прислал» и мусор — ноль, отрицательных не бывает.
const count = (v) => {
  const n = intOrNull(v);
  return n != null && n > 0 ? n : 0;
};
const isObj = (v) => !!v && typeof v === 'object' && !Array.isArray(v);
const str = (v) => (v == null ? '' : String(v));

export function publicPlanOf(o) {
  if (!isObj(o) || !o.slug) return null;
  const periodDays = intOrNull(o.period_days);
  const months = intOrNull(o.months);
  const startsAfter = !!o.starts_after_current;
  const startsAt = o.sale_starts_at ? String(o.sale_starts_at) : null;
  const endsAt = o.sale_ends_at ? String(o.sale_ends_at) : null;
  return {
    slug: String(o.slug),
    name: o.name ? String(o.name) : String(o.slug),
    price_rub: numOrNull(o.price_rub),
    period_days: periodDays,
    months: months != null && months >= 0
      ? months
      : (periodDays ? Math.max(1, Math.round(periodDays / 30)) : null),
    per_month_rub: numOrNull(o.per_month_rub),
    discount_pct: intOrNull(o.discount_pct),
    renewable: !!o.renewable,
    audience: str(o.audience),
    chat_messages_per_30d: intOrNull(o.chat_messages_per_30d),
    voice_seconds_per_30d: intOrNull(o.voice_seconds_per_30d),
    max_documents: intOrNull(o.max_documents),
    max_storage_mb: intOrNull(o.max_storage_mb),
    max_file_size_mb: intOrNull(o.max_file_size_mb),
    // Словарь целиком, включая нефлаговые ключи (`expiry_notice_days`): редактор
    // показывает только флаги, а остальное сервер сохраняет сам.
    features: isObj(o.features) ? { ...o.features } : {},
    is_active: !!o.is_active,
    is_default: !!o.is_default,
    is_paid: o.is_paid != null ? !!o.is_paid : (numOrNull(o.price_rub) || 0) > 0,
    // Дефолт модели — «показывать»; поле новое, и старый ответ без него значит
    // «как было до флага», то есть в витрине.
    show_in_storefront: typeof o.show_in_storefront === 'boolean' ? o.show_in_storefront : true,
    starts_after_current: startsAfter,
    sale_starts_at: startsAt,
    sale_ends_at: endsAt,
    // Продаётся ли прямо сейчас — слово СЕРВЕРА (его часы). Не прислал — null,
    // и только тогда статус решают часы браузера.
    on_sale: typeof o.on_sale === 'boolean' ? o.on_sale : null,
    is_promo: o.is_promo != null ? !!o.is_promo : !!(startsAfter || startsAt || endsAt),
    sales_note: str(o.sales_note),
    workspaces_count: count(o.workspaces_count),
    auto_renew_count: count(o.auto_renew_count),
    pinned_count: count(o.pinned_count),
    queued_count: count(o.queued_count),
    pending_payments_count: count(o.pending_payments_count),
    live_links_count: count(o.live_links_count),
    // Сколько подписок закрепит прежнюю цену, если сменить её СЕЙЧАС, — число
    // сервера (тот же предикат, что у закрепления); экран печатает его как есть.
    pin_on_change_count: count(o.pin_on_change_count),
    frozen_fields: Array.isArray(o.frozen_fields) ? o.frozen_fields.filter((f) => typeof f === 'string') : [],
    // Адрес собирает только бэкенд (FRONTEND_URL); пусто — ссылки нет (бесплатный,
    // не продаваемый по ссылке).
    buy_url: typeof o.buy_url === 'string' && o.buy_url ? o.buy_url : null,
  };
}

export function publicPlansOf(data) {
  const box = isObj(data) ? data : {};
  const rawGroups = Array.isArray(box.groups) ? box.groups : [];
  const groups = rawGroups.map((g) => {
    const grp = isObj(g) ? g : {};
    const families = (Array.isArray(grp.families) ? grp.families : []).map((f) => {
      const fam = isObj(f) ? f : {};
      const plans = (Array.isArray(fam.plans) ? fam.plans : []).map(publicPlanOf).filter(Boolean);
      const first = plans[0] || {};
      return {
        key: fam.key ? String(fam.key) : (first.slug || ''),
        name: fam.name ? String(fam.name) : (first.name || first.slug || ''),
        is_promo: fam.is_promo != null ? !!fam.is_promo : !!first.is_promo,
        plans,
      };
    }).filter((f) => f.plans.length > 0);
    const audience = str(grp.audience);
    return {
      audience,
      label: grp.label ? String(grp.label) : (PUBLIC_AUDIENCE_LABELS[audience] || audience),
      families,
    };
  }).filter((g) => g.families.length > 0);
  const features_catalog = (Array.isArray(box.features_catalog) ? box.features_catalog : [])
    .filter((c) => isObj(c) && c.key)
    .map((c) => ({ key: String(c.key), label: c.label ? String(c.label) : String(c.key) }));
  return { groups, features_catalog };
}

// Что сделал сервер при сохранении: сколько подписок закрепили прежнюю цену и у
// скольких пространств сразу сменились лимиты. Мусор — ноль: тост не обещает
// закрепления, которого не было.
export function saveEffectsOf(data) {
  const d = isObj(data) ? data : {};
  return { pinned_now: count(d.pinned_now), affected_now: count(d.affected_now) };
}

/* ── Поиск клиента (право на любой из двух разделов) ── */

// GET /console/clients/?q= → {results:[{id,email,full_name,workspaces:[...]}]}
// Меньше 3 символов сервер не ищет и возвращает code:'query_too_short' с пустым списком.
export const searchConsoleClients = (q) =>
  apiFetch(`/api/subscription/console/clients/?q=${encodeURIComponent(q)}`);

/* ── Ссылки на оплату (право subscriptions.issue_payment_links) ── */

// GET /console/payment-links/?status=&q= → {links:[...], ttl_default_days, ttl_max_days}
export const listPaymentLinks = ({ status = '', q = '' } = {}) => {
  const params = new URLSearchParams();
  if (status) params.set('status', status);
  if (q) params.set('q', q);
  const qs = params.toString();
  return apiFetch(`/api/subscription/console/payment-links/${qs ? `?${qs}` : ''}`);
};

// POST /console/payment-links/ {client_id, workspace_id, plan_slug, ttl_days, note,
//   send_to_client, mode, new_workspace_name} → 201 {url, delivery:{email,telegram}, ...}
// `mode`: 'existing' (по умолчанию — тариф существующему пространству) либо
// 'new_workspace' — бизнес-пространство создаётся ПОСЛЕ оплаты; тогда
// `workspace_id` не нужен, а `new_workspace_name` — необязательная заготовка
// названия, которую клиент подтвердит или поправит на странице оплаты.
// `send_to_client` (по умолчанию true) — отправить ссылку клиенту письмом и, если
// привязан Telegram, сообщением в бот. Сбой доставки НЕ отменяет выпуск: ссылка
// уже создана, а результат по каждому каналу приходит в `delivery`.
export const createPaymentLink = (payload) =>
  apiFetch('/api/subscription/console/payment-links/', { method: 'POST', body: payload });

// POST /pay/<token>/invoice/ {requisites, offer_accepted} → 201 {invoice_id, ...}
// Второй способ оплаты той же ссылки — безнал-счёт (Точка). Тариф, пространство
// и сумма берутся ИЗ ССЫЛКИ; тело совпадает с /invoice/checkout/, чтобы форма
// реквизитов переиспользовалась без ветвлений.
// `workspaceName` — только для ссылок в режиме «новое пространство»: название
// заведения, которое клиент подтвердил перед оплатой. Уезжает вместе с началом
// оплаты, а не отдельным запросом: пространство создаётся в вебхуке провайдера,
// где ни браузера клиента, ни его сессии уже нет.
export const startPaymentLinkInvoice = (token, requisites, workspaceName) =>
  apiFetch(`/api/subscription/pay/${encodeURIComponent(token)}/invoice/`, {
    method: 'POST',
    body: {
      requisites,
      offer_accepted: true,
      ...(workspaceName ? { workspace_name: workspaceName } : {}),
    },
  });

// POST /console/payment-links/<id>/revoke/ → карточка ссылки. Идемпотентно;
// уже оплаченную ссылку сервер отбивает кодом link_already_used.
export const revokePaymentLink = (id) =>
  apiFetch(`/api/subscription/console/payment-links/${encodeURIComponent(id)}/revoke/`, {
    method: 'POST', body: {},
  });

/* ── Клиентская сторона именной ссылки ── */

// GET /pay/<token>/ — открыт БЕЗ логина. Счёт на СУЩЕСТВУЮЩЕЕ пространство
// отдаётся целиком любому, у кого ссылка (payable_by_anyone=true): оплатить
// его вправе бухгалтер или учредитель, а оплатить, не видя суммы, нельзя.
// Скрыт от постороннего только `current_plan` — это состояние подписки
// клиента, к составу счёта отношения не имеющее. Режим «новое пространство»
// по-прежнему урезан до статуса, срока и маски email.
export const getPaymentLink = (token) =>
  apiFetch(`/api/subscription/pay/${encodeURIComponent(token)}/`);

// POST /pay/<token>/checkout/ → 201 {redirect_url, invoice_id, amount_rub}.
// БЕЗ логина для ссылок на существующее пространство; в режиме «новое
// пространство» по-прежнему нужен вход под клиентом (иначе 403 wrong_account).
// method: 'card' (по умолчанию) | 'foreign_card' — оплата из-за рубежа инокартой,
// нерекуррентно. Контракт тот же, что у самообслуживания /checkout/.
// consent: true — плательщик поставил галочку согласия на автосписания
// (RecurringConsentModal). Ссылке с автопродлением без неё сервер откажет
// (400 recurring_consent_required); разовой ссылке и инокарте она не нужна.
export const startPaymentLinkCheckout = (token, method = 'card', workspaceName, consent) =>
  apiFetch(`/api/subscription/pay/${encodeURIComponent(token)}/checkout/`, {
    method: 'POST',
    body: {
      method,
      ...(workspaceName ? { workspace_name: workspaceName } : {}),
      ...consentBody(consent),
    },
  });

/* ── Чтение состояния оплаты ПО ТОКЕНУ (для того, у кого нет аккаунта) ──
   Обычные /payments/<id>/ и /invoice/<id>/ скоупятся по сессии и посторонему
   плательщику отвечают 404. Без этих двух он после оплаты упирается в /signin
   и читает это как «деньги не прошли» — то есть платит второй раз. */

// GET /pay/<token>/payment/<invoice_id>/ — ответ той же формы, что у
// /payments/<invoice_id>/, чтобы страница успеха отличалась только источником.
export const getPaymentLinkPaymentStatus = (token, invoiceId) =>
  apiFetch(`/api/subscription/pay/${encodeURIComponent(token)}`
    + `/payment/${encodeURIComponent(invoiceId)}/`);

// GET /pay/<token>/invoice/<invoice_id>/ — состояние безнал-счёта (Точка).
export const getPaymentLinkInvoice = (token, invoiceId) =>
  apiFetch(`/api/subscription/pay/${encodeURIComponent(token)}`
    + `/invoice/${encodeURIComponent(invoiceId)}/`);

// Прямая ссылка на PDF счёта — открывается тегом <a>, без заголовков.
export const paymentLinkInvoicePdfUrl = (token, invoiceId) =>
  `/api/subscription/pay/${encodeURIComponent(token)}`
  + `/invoice/${encodeURIComponent(invoiceId)}/pdf/`;
